/**
 * RegistryCertificatePremium — Iter V2 Certificate Claim.
 *
 * Public certificate landing + claim conversion experience for HC/SC
 * registry certificates. Redesigned per the CollectorUp Certificate Claim
 * V2 spec:
 *
 *   1. Celebration Hero + VERIFIED seal
 *   2. Primary Claim Card (ABOVE the certificate on mobile)
 *   3. Three-step "How it works"
 *   4. Certificate Presentation (portrait, framed, object-contain)
 *   5. Official Certification card
 *   6. The Certified Album card
 *   7. Achievement Unlocked
 *   8. The Journey to Completion
 *   9. Claim More Than a Certificate (LIVE capabilities only)
 *  10. Welcome to Your Collector Legacy
 *  11. Final Claim CTA
 *  12. Sticky mobile Claim bar (unclaimed only)
 *
 * The certificate remains the emotional payoff, not the claim button.
 * Zero backend changes; every field read comes from the existing
 * `/api/public/registry/certificates/{n}` endpoint.
 */
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Copy, User, Award, Trophy, ShieldCheck,
  Sparkles, Route, Compass, PackageOpen, Repeat, Lock,
} from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import OfficialCertificateFrame from "@/components/OfficialCertificateFrame";
import ClaimCTA from "@/components/claim/ClaimCTA";
import StickyMobileClaimCTA from "@/components/claim/StickyMobileClaimCTA";
import { getAlbum } from "@/config/albumCatalog";
import {
  friendlyAlbumName,
  coverTypeLabel,
  formatCertNumber,
  formatCompletionDate,
  certificateTemplateAssets,
} from "@/lib/certificateFormat";
import CollectorProfilePreview from "@/components/certificates/CollectorProfilePreview";
import MobileHeroClaimCTA from "@/components/claim/MobileHeroClaimCTA";
import { isFoundingCollector, FutureVisionPreview, FounderClosing } from "@/components/collector/CollectionLaunchExperience";

const AlbumLabels = { HC: "Hardcover Album", SC: "Softcover Album" };

function fmtDate(iso) {
  return formatCompletionDate(iso) || null;
}

export default function RegistryCertificatePremium({ cert }) {
  const [copied, setCopied] = useState(false);
  const primaryClaimRef = useRef(null);
  const finalClaimRef = useRef(null);

  const certNumber = formatCertNumber(cert.cert_number);
  const publicUrl = `${window.location.origin}/certificates/${certNumber}`;
  const albumLabel = AlbumLabels[cert.album_type] || "Album";
  const coverLabel = coverTypeLabel(cert.album_type);
  const album = getAlbum(cert.album_slug);
  const templateAssets = certificateTemplateAssets(cert.album_slug, cert.album_type);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Couldn't copy."); }
  };

  const collectorName = (cert.collector_name || "").trim();
  const collectorFirstName = collectorName.split(/\s+/)[0] || "";
  // Phase 4B — every album surface now uses the friendly frontend label.
  const albumTitle = friendlyAlbumName(cert.album_slug) || album?.label || "the album";

  return (
    <div className="min-h-screen bg-[var(--ink)]" data-testid="registry-cert-premium">
      <SEO
        title={`${cert.cert_number} · Verified Album · CollectorUp`}
        description={`View an officially inspected and registered album completion certificate on CollectorUp.`}
        url={publicUrl}
      />

      {/* Ambient radial gold light behind the hero — restrained, once */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[560px] opacity-70"
           style={{
             background:
               "radial-gradient(60% 60% at 50% 0%, rgba(200,162,77,0.18) 0%, rgba(200,162,77,0.05) 40%, transparent 70%)",
           }} />

      <div className="relative container-gm max-w-5xl px-4 pt-4 pb-32 md:pt-10 md:pb-24">
        <Link
          to="/certificates"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white text-xs uppercase tracking-widest"
          data-testid="registry-cert-back"
        >
          <ArrowLeft size={14} /> Certificates
        </Link>

        {/* ─────────────── 1. CELEBRATION HERO — Collector-first ─────────────── */}
        <section className="mt-4 md:mt-14 text-center" data-testid="registry-cert-hero">
          <VerifiedSeal />
          <p
            className="mt-3 md:mt-6 text-[10px] md:text-xs uppercase tracking-[0.35em] text-[var(--collector-gold-soft)]"
            data-testid="registry-cert-hero-eyebrow"
          >
            Welcome to Your Digital Collector Home
          </p>

          {/* HERO — Collector Name (largest, heaviest element on the page) */}
          <h1
            data-testid="registry-cert-hero-name"
            className="mt-3 md:mt-6 font-display font-bold text-white leading-[0.95] text-3xl md:text-7xl lg:text-8xl break-words"
            style={{ letterSpacing: "-0.02em" }}
          >
            {collectorName || "A Verified Collector"}
          </h1>

          {/* Verified + Founding + Since strip — desktop only; mobile hides
              this row to make room for the above-the-fold Google CTA. */}
          <div className="hidden md:flex mt-5 flex-wrap items-center justify-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-3 py-1 text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-300"
                  data-testid="registry-cert-hero-verified">
              <ShieldCheck size={11} /> Officially Verified
            </span>
            {cert.is_claimed && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--collector-gold)]/50 bg-[var(--collector-gold)]/12 px-3 py-1 text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--collector-gold-soft)]"
                    data-testid="registry-cert-hero-founding"
                    title="Founding Collector — one of the first verified collectors on CollectorUp.">
                <Sparkles size={11} /> Founding Collector
              </span>
            )}
            {cert.tournament_year && (
              <span className="text-[10px] uppercase tracking-[0.24em] text-white/50" data-testid="registry-cert-hero-since">
                Collector Since {cert.tournament_year}
              </span>
            )}
          </div>

          {/* Mobile-compact verified badge — single row */}
          <div className="md:hidden mt-3 flex justify-center">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-300">
              <ShieldCheck size={11} /> Officially Verified
            </span>
          </div>

          {/* Description — desktop only; mobile drops it to keep the
              Google CTA above the fold. */}
          <p className="hidden md:block mt-5 mx-auto max-w-xl text-sm md:text-base text-white/70">
            Your verified album certificate is now part of your permanent CollectorUp profile.
          </p>

          {/* Certificate Number — labelled block per spec */}
          <div className="mt-3 md:mt-8 inline-flex flex-col items-center gap-1" data-testid="registry-cert-hero-number-block">
            <div className="text-[10px] md:text-xs uppercase tracking-[0.35em] text-white/50">
              Certificate Number
            </div>
            <div
              className="font-mono font-bold text-xl md:text-3xl text-[var(--collector-gold-soft)]"
              data-testid="registry-cert-hero-number"
            >
              {certNumber}
            </div>
          </div>

          {/* Mobile-only above-the-fold Google CTA — hero-level primary
              claim action so the collector never has to scroll on a
              phone to reach it. */}
          <MobileHeroClaimCTA certNumber={cert.cert_number} isClaimed={cert.is_claimed} />

          {/* Album (friendly name) + Completion date — desktop only.
              Mobile shows this after the CTA (below the fold) since the
              certificate metadata is repeated on the certificate art. */}
          <div className="hidden md:block mt-6 mx-auto max-w-2xl">
            <div className="text-base md:text-lg text-white/85 font-display leading-tight" data-testid="registry-cert-hero-album">
              {albumTitle}
              {coverLabel ? <span className="text-white/50"> · {coverLabel}</span> : null}
            </div>
            {fmtDate(cert.completion_date) && (
              <div className="mt-2 text-xs md:text-sm text-white/60" data-testid="registry-cert-hero-completion">
                Completed {fmtDate(cert.completion_date)}
              </div>
            )}
          </div>
        </section>

        {/* ─────────────── 2. PRIMARY CLAIM CARD ─────────────── */}
        {!cert.is_claimed && (
          <section
            ref={primaryClaimRef}
            className="mt-10 md:mt-14 rounded-3xl border border-[var(--collector-gold)]/30 bg-gradient-to-b from-[#1a1a1a] to-[#0e0e0e] p-6 md:p-10 shadow-[0_20px_60px_rgba(0,0,0,0.55)]"
            data-testid="registry-cert-primary-claim"
          >
            <div className="text-center max-w-2xl mx-auto">
              <p className="text-[10px] uppercase tracking-[0.35em] text-[var(--collector-gold)]">
                One Last Step
              </p>
              <h2 className="mt-3 font-display text-2xl md:text-4xl text-white">
                Your Collector Profile Is Waiting
              </h2>
              <p className="mt-4 text-sm md:text-base text-white/70">
                This certificate is ready to be linked to its rightful owner.
              </p>
              <div className="mt-8">
                <ClaimCTA certNumber={cert.cert_number} testidPrefix="primary-claim" />
              </div>
            </div>
          </section>
        )}

        {cert.is_claimed && (
          <section className="mt-10 md:mt-14" data-testid="registry-cert-claimed">
            <CollectorProfilePreview
              is_claimed={true}
              owner_public_collector_id={cert.owner_public_collector_id}
              collector_name={cert.collector_name}
              theme="dark"
            />
          </section>
        )}

        {/* ─────────────── 3. HOW IT WORKS ─────────────── */}
        {!cert.is_claimed && (
          <section className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-testid="registry-cert-steps">
            <Step n="1" title="Create or sign in" body="Free CollectorUp account. Under 60 seconds." />
            <Step n="2" title="Confirm ownership" body="Enter the private claim code sent with your certificate." />
            <Step n="3" title="Unlock your profile" body="Your verified collector profile goes live." />
          </section>
        )}

        {/* ─────────────── 4. CERTIFICATE PRESENTATION ─────────────── */}
        <section className="mt-16 md:mt-24" data-testid="registry-cert-artwork">
          <SectionEyebrow>Your Official Certificate</SectionEyebrow>
          <div className="mt-6 mx-auto max-w-2xl">
            <div className="rounded-3xl border-2 border-[var(--collector-gold)]/40 bg-white p-3 md:p-4 shadow-[0_30px_80px_-20px_rgba(200,162,77,0.35)]">
              {templateAssets?.certificateImageUrl ? (
                <OfficialCertificateFrame
                  src={templateAssets.certificateImageUrl}
                  alt={templateAssets.certificateAlt || `Official CollectorUp ${coverLabel} Certificate — Sample Preview`}
                />
              ) : (
                <div className="aspect-[3/4] rounded-2xl bg-[var(--stone)]/30 grid place-items-center text-[var(--ink)]/40 text-sm">
                  Certificate preview unavailable for this album.
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ─────────────── 5. OFFICIAL CERTIFICATION ─────────────── */}
        <section className="mt-16 md:mt-24" data-testid="registry-cert-official">
          <SectionEyebrow>Official Certification</SectionEyebrow>
          <div className="mt-6 rounded-3xl border-2 border-white/10 bg-white p-6 md:p-8 text-[var(--ink)]">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 text-sm">
              {cert.collector_name && <Fact label="Collector">{cert.collector_name}</Fact>}
              <Fact label="Certificate number" mono>{certNumber}</Fact>
              <Fact label="Album">{albumTitle}{coverLabel ? ` — ${coverLabel}` : ""}</Fact>
              <Fact label="Verification status">
                <span className="inline-flex items-center gap-1.5 text-emerald-800 font-bold">
                  <ShieldCheck size={16} /> Verified
                </span>
              </Fact>
              {cert.tournament_year && <Fact label="Tournament year">{cert.tournament_year}</Fact>}
              {fmtDate(cert.completion_date) && <Fact label="Inspection date">{fmtDate(cert.completion_date)}</Fact>}
              <Fact label="Verified by">Gil Maakaroun · CollectorUp</Fact>
            </dl>
            <button
              onClick={copyLink}
              data-testid="registry-cert-copy-link"
              className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--smoke)] hover:text-[var(--ink)]"
            >
              <Copy size={14} /> {copied ? "Link copied" : "Copy certificate link"}
            </button>
          </div>
        </section>

        {/* ─────────────── 6. THE CERTIFIED ALBUM ─────────────── */}
        <section className="mt-16 md:mt-24" data-testid="registry-cert-album">
          <SectionEyebrow>The Certified Album</SectionEyebrow>
          <div className="mt-6 mx-auto max-w-md">
            <div className="rounded-3xl border-2 border-white/10 bg-white p-4">
              <div className="aspect-[3/4] bg-neutral-50 rounded-xl overflow-hidden flex items-center justify-center">
                {templateAssets?.albumCoverUrl ? (
                  <img
                    src={templateAssets.albumCoverUrl}
                    alt={templateAssets.albumAlt || `${albumTitle} — ${coverLabel}`}
                    className="max-w-full max-h-full w-auto h-auto object-contain object-center"
                    data-testid="registry-cert-album-cover"
                  />
                ) : (
                  <PackageOpen size={44} className="text-[var(--ink)]/30" />
                )}
              </div>
              <div className="mt-4 text-center">
                <div className="font-display text-xl leading-tight text-[var(--ink)]">
                  {albumTitle}
                </div>
                <div className="mt-1 text-[11px] uppercase tracking-widest text-[var(--smoke)]">
                  {coverLabel || albumLabel}{cert.tournament_year ? ` · ${cert.tournament_year}` : ""}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─────────────── 7. ACHIEVEMENT UNLOCKED ─────────────── */}
        <section className="mt-16 md:mt-24 text-center" data-testid="registry-cert-achievement">
          <div className="inline-flex items-center gap-2 rounded-full border border-[var(--collector-gold)]/40 px-4 py-1.5 text-[10px] uppercase tracking-[0.35em] text-[var(--collector-gold)]">
            <Trophy size={12} /> Achievement Unlocked
          </div>
          <h2 className="mt-6 font-display text-3xl md:text-5xl text-white leading-tight">
            Album Completed
          </h2>
          <p className="mt-4 mx-auto max-w-xl text-white/70">
            {collectorFirstName
              ? `${collectorFirstName}, this achievement is now part of your collector history.`
              : "This achievement is now part of the collector's history."}{" "}
            Your collection has been inspected, verified, and added to the official CollectorUp Certificates.
          </p>
        </section>

        {/* ─────────────── 8. JOURNEY TO COMPLETION ─────────────── */}
        <section className="mt-16 md:mt-24" data-testid="registry-cert-journey">
          <SectionEyebrow>The Journey to Completion</SectionEyebrow>
          <p className="mt-3 max-w-xl mx-auto text-center text-white/60 text-sm">
            Every completed album represents time, patience, trades, memories, and dedication.
          </p>
          <ol className="mt-8 relative grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[
              { icon: PackageOpen, label: "Opened Packs" },
              { icon: Repeat, label: "Traded Stickers" },
              { icon: Compass, label: "Found Missing Pieces" },
              { icon: Award, label: "Completed the Album" },
              { icon: ShieldCheck, label: "Verified by CollectorUp" },
              { icon: Route, label: "Officially Registered" },
            ].map((m, i) => (
              <li key={i} className="rounded-2xl border border-white/10 bg-white/5 p-5 flex items-center gap-3">
                <m.icon size={20} className="text-[var(--collector-gold)] shrink-0" aria-hidden />
                <span className="text-white/85 font-display text-base">{m.label}</span>
              </li>
            ))}
          </ol>
        </section>

        {/* ─────────────── 9. CLAIM MORE THAN A CERTIFICATE ─────────────── */}
        {!cert.is_claimed && (
          <section className="mt-16 md:mt-24" data-testid="registry-cert-benefits">
            <SectionEyebrow>Claim More Than a Certificate</SectionEyebrow>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              <Benefit icon={User} title="Verified Collector Profile" body="Create your permanent CollectorUp identity." />
              <Benefit icon={PackageOpen} title="Your Album Collection" body="Display the albums that are part of your collecting journey." />
              <Benefit icon={Trophy} title="Achievements" body="Celebrate completed albums and collector milestones." />
              <Benefit icon={Award} title="Trophy Case" body="Keep your verified accomplishments together." />
              <Benefit icon={Sparkles} title="Shareable Profile" body="Share your collection with family, friends, and collectors." />
              <Benefit icon={Lock} title="Privacy Controls" body="Control the information shown publicly." />
            </div>
            <p className="mt-8 text-center text-xs md:text-sm text-white/50 max-w-xl mx-auto">
              CollectorUp will continue to grow, and eligible new collector tools will become available through your account as they launch.
            </p>
          </section>
        )}

        {/* ─────────────── 10. COLLECTOR LEGACY ─────────────── */}
        <section className="mt-16 md:mt-24 text-center" data-testid="registry-cert-legacy">
          <SectionEyebrow>Welcome to Your Collector Legacy</SectionEyebrow>
          <p className="mt-6 mx-auto max-w-2xl text-white/75 leading-relaxed">
            Today, this album became part of the CollectorUp Certificates.
            Your collecting journey does not end here. Future verified albums,
            achievements, and milestones can continue building your permanent
            collector history.
          </p>
        </section>

        {/* ─────────────── 11. FINAL CLAIM CTA ─────────────── */}
        {!cert.is_claimed && (
          <section
            ref={finalClaimRef}
            className="mt-16 md:mt-24 rounded-3xl border border-[var(--collector-gold)]/30 bg-gradient-to-b from-[#1a1a1a] to-[#0e0e0e] p-8 md:p-12"
            data-testid="registry-cert-final-claim"
          >
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="font-display text-2xl md:text-4xl text-white">
                Ready to unlock your verified profile?
              </h2>
              <div className="mt-8">
                <ClaimCTA certNumber={cert.cert_number} testidPrefix="final-claim" />
              </div>
            </div>
          </section>
        )}

        {/* ─────────────── 12. WHAT'S COMING TO COLLECTORUP ─────────────── */}
        <section className="mt-16 md:mt-24" data-testid="registry-cert-future">
          <FutureVisionPreview />
        </section>

        {/* ─────────────── 13. FOUNDER CLOSING ─────────────── */}
        <section className="mt-8" data-testid="registry-cert-founder-closing">
          <FounderClosing name={collectorFirstName || null} publicVariant={!cert.is_claimed} />
        </section>

        {/* Certificate Purchasing CTA — a tasteful invitation that
            sits AFTER the primary claim CTA / founder closing so it
            never competes with "Claim My Profile" on an unclaimed
            certificate. Public copy only — no owner data leaks. */}
        <section className="mt-14" data-testid="registry-cert-order-cta">
          <div className="mx-auto max-w-2xl rounded-3xl border border-[var(--gm-gold)]/40 bg-[rgba(230,184,72,0.08)] p-5 md:p-6 text-center">
            <div className="text-[10px] uppercase tracking-widest text-[var(--gm-gold)] font-bold">Album Complete Certificate</div>
            <h3 className="mt-2 font-serif text-2xl md:text-3xl text-white">
              Completed your album too?
            </h3>
            <p className="mt-2 text-sm text-white/75">
              Get your official CollectorUp certificate. Order for $10.70,
              send a short verification video, and we&apos;ll ship it after review.
            </p>
            <a
              href="/certificates/order"
              data-testid="registry-cert-order-cta-link"
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-2xl bg-[var(--gm-gold)] text-[var(--ink)] font-display uppercase tracking-widest text-sm px-5 py-3 hover:brightness-110 transition"
            >
              Order Your Certificate — $10.70
            </a>
          </div>
        </section>

        {/* Disclaimer */}
        <p className="mt-16 text-center text-[11px] leading-relaxed text-white/40 max-w-2xl mx-auto" data-testid="registry-cert-disclaimer">
          CollectorUp is an independent album inspection and registry service. Not affiliated with, endorsed by, or sponsored by Panini S.p.A., FIFA, or any World Cup organising body. All trademarks belong to their respective owners.
        </p>
      </div>

      {/* Sticky mobile CTA — unclaimed only */}
      <StickyMobileClaimCTA
        certNumber={cert.cert_number}
        primaryRef={primaryClaimRef}
        finalRef={finalClaimRef}
        hidden={cert.is_claimed}
      />
    </div>
  );
}

// ─── small local presentational helpers ────────────────────────

function VerifiedSeal() {
  return (
    <div className="mx-auto inline-flex flex-col items-center" aria-label="Verified by CollectorUp">
      <div
        className="relative w-24 h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center border-2 border-[var(--collector-gold)]"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, #E4C989 0%, #C8A24D 40%, #8F7328 100%)",
          boxShadow: "0 8px 30px rgba(200,162,77,0.35), inset 0 2px 8px rgba(255,255,255,0.35)",
        }}
      >
        <ShieldCheck size={40} className="text-[var(--ink)]" strokeWidth={2.5} aria-hidden />
      </div>
      <div className="mt-2 text-[10px] uppercase tracking-[0.4em] text-[var(--collector-gold-soft)]">Verified</div>
    </div>
  );
}

function SectionEyebrow({ children }) {
  return (
    <div className="text-center">
      <div className="inline-flex items-center gap-3 text-[10px] md:text-xs uppercase tracking-[0.35em] text-[var(--collector-gold)]">
        <span className="w-6 h-px bg-[var(--collector-gold)]/50" aria-hidden />
        {children}
        <span className="w-6 h-px bg-[var(--collector-gold)]/50" aria-hidden />
      </div>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5" data-testid={`registry-cert-step-${n}`}>
      <div className="inline-flex items-center gap-3">
        <span className="w-8 h-8 rounded-full bg-[var(--collector-gold)] text-[var(--ink)] font-display font-bold flex items-center justify-center">{n}</span>
        <span className="font-display text-lg text-white">{title}</span>
      </div>
      <p className="mt-3 text-sm text-white/60">{body}</p>
    </div>
  );
}

function Fact({ label, children, mono }) {
  if (children == null || children === "") return null;
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">{label}</dt>
      <dd className={"mt-1 " + (mono ? "font-mono" : "font-medium")}>{children}</dd>
    </div>
  );
}

function Benefit({ icon: Icon, title, body }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5" data-testid="registry-cert-benefit">
      <Icon size={22} className="text-[var(--collector-gold)]" aria-hidden />
      <div className="mt-3 font-display text-lg text-white">{title}</div>
      <p className="mt-1.5 text-sm text-white/60">{body}</p>
    </div>
  );
}
